import React, { useState, useEffect } from 'react';
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	TextField,
	Button,
	List,
	ListItem,
	ListItemText,
	ListItemSecondaryAction,
	Checkbox,
	CircularProgress,
	Typography,
	Box,
	Chip,
	Divider,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ProductIcon from '@mui/icons-material/Inventory';
import apiClient from '../services/apiClient';
import notify from '../services/notify';
import { getCurrency } from '../services/tableHelper';
import { useSelector } from 'react-redux';

interface ManageProductsModalProps {
	isOpen: boolean;
	onClose: () => void;
	productCategory: any;
	onProductsUpdated: () => void;
}

interface Product {
	id: number;
	name: string;
	description?: string;
	default_price?: number;
	category_id?: number;
}

export default function ManageProductsModal({
	isOpen,
	onClose,
	productCategory,
	onProductsUpdated,
}: ManageProductsModalProps) {
	const currency = useSelector((state: any) => getCurrency(state.auth.user.currency));
	const [searchTerm, setSearchTerm] = useState('');
	const [allProducts, setAllProducts] = useState<Product[]>([]);
	const [productsInCategory, setProductsInCategory] = useState<Product[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [selectedProductIds, setSelectedProductIds] = useState<Set<number>>(new Set());
	const [loadingProductIds, setLoadingProductIds] = useState<Set<number>>(new Set());

	useEffect(() => {
		if (isOpen) {
			fetchData();
		}
	}, [isOpen, productCategory]);

	const fetchData = async () => {
		setIsLoading(true);
		try {
			// Fetch all products and products in this category in parallel
			const [allProductsResponse, categoryProductsResponse] = await Promise.all([
				apiClient.get('/products'),
				productCategory?.id
					? apiClient.get(`/product-categories/${productCategory.id}/products`)
					: Promise.resolve({ data: [] }),
			]);

			const allProductsData = allProductsResponse.data || [];
			const categoryProductsData = categoryProductsResponse.data || [];

			setAllProducts(allProductsData);
			setProductsInCategory(categoryProductsData);

			// Initialize selected products with those already in the category
			setSelectedProductIds(new Set(categoryProductsData.map((p: Product) => p.id)));
		} catch (error) {
			console.error('Error fetching products:', error);
			notify('error', 'Failed to load products');
		} finally {
			setIsLoading(false);
		}
	};

	const handleProductToggle = async (productId: number) => {
		const isCurrentlySelected = selectedProductIds.has(productId);

		// Add to loading state
		setLoadingProductIds((prev) => new Set(prev).add(productId));

		try {
			if (isCurrentlySelected) {
				// Remove product from category (set category_id to null)
				await updateProductCategory(productId, null);
				const newSelected = new Set(selectedProductIds);
				newSelected.delete(productId);
				setSelectedProductIds(newSelected);

				// Remove from productsInCategory state
				setProductsInCategory((prev) => prev.filter((p) => p.id !== productId));
			} else {
				// Add product to category
				await updateProductCategory(productId, productCategory.id);
				const newSelected = new Set(selectedProductIds);
				newSelected.add(productId);
				setSelectedProductIds(newSelected);

				// Add to productsInCategory state
				const product = allProducts.find((p) => p.id === productId);
				if (product) {
					setProductsInCategory((prev) => [...prev, product]);
				}
			}

			onProductsUpdated(); // Refresh parent component
		} catch (error: any) {
			console.error('Error updating product assignment:', error);
			notify('error', error.response?.data?.message || 'Failed to update product assignment');
		} finally {
			// Remove from loading state
			setLoadingProductIds((prev) => {
				const newSet = new Set(prev);
				newSet.delete(productId);
				return newSet;
			});
		}
	};

	const updateProductCategory = async (productId: number, categoryId: number | null) => {
		await apiClient.update(`/products/${productId}`, {
			category_id: categoryId,
		});
	};

	const handleClose = () => {
		onClose();
	};

	// Filter products based on search term
	const filteredProducts = allProducts.filter(
		(product) =>
			product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
			(product.description && product.description.toLowerCase().includes(searchTerm.toLowerCase()))
	);

	// Separate products into those in category and those not in category for better organization
	const productsInCategoryFiltered = filteredProducts.filter((p) => productsInCategory.some((cp) => cp.id === p.id));
	const productsNotInCategoryFiltered = filteredProducts.filter(
		(p) => !productsInCategory.some((cp) => cp.id === p.id)
	);

	return (
		<Dialog
			open={isOpen}
			onClose={onClose}
			maxWidth="md"
			fullWidth
			PaperProps={{
				style: {
					minHeight: '600px',
					maxHeight: '80vh',
				},
			}}
		>
			<DialogTitle>
				<Box display="flex" alignItems="center" gap={1}>
					<ProductIcon />
					<span>Manage Products - {productCategory?.name}</span>
				</Box>
			</DialogTitle>

			<DialogContent>
				{/* Search Field */}
				<TextField
					fullWidth
					placeholder="Search products by name or description..."
					value={searchTerm}
					onChange={(e) => setSearchTerm(e.target.value)}
					InputProps={{
						startAdornment: <SearchIcon style={{ marginRight: '8px', color: '#9ca3af' }} />,
					}}
					style={{ marginBottom: '20px' }}
				/>

				{isLoading ? (
					<Box display="flex" justifyContent="center" py={4}>
						<CircularProgress />
					</Box>
				) : (
					<>
						{/* Summary */}
						<Box mb={2}>
							<Chip
								label={`${selectedProductIds.size} product${
									selectedProductIds.size !== 1 ? 's' : ''
								} selected`}
								color="primary"
								variant="outlined"
							/>
						</Box>

						{/* Products currently in category */}
						{productsInCategoryFiltered.length > 0 && (
							<>
								<Typography
									variant="subtitle2"
									gutterBottom
									style={{ fontWeight: 'bold', color: '#374151' }}
								>
									Currently in "{productCategory?.name}" ({productsInCategoryFiltered.length})
								</Typography>
								<List dense style={{ maxHeight: '200px', overflow: 'auto', marginBottom: '16px' }}>
									{productsInCategoryFiltered.map((product) => (
										<ListItem
											key={product.id}
											component="div"
											style={{
												backgroundColor: selectedProductIds.has(product.id)
													? '#f0f9ff'
													: '#fff3cd',
												borderRadius: '4px',
												marginBottom: '2px',
												border: selectedProductIds.has(product.id)
													? '1px solid #0ea5e9'
													: '1px solid #fbbf24',
												cursor: 'pointer',
											}}
											onClick={() => handleProductToggle(product.id)}
										>
											<ListItemText
												primary={product.name}
												secondary={
													<span>
														{product.description && <span>{product.description}</span>}
														{product.default_price && (
															<span style={{ color: '#059669', fontSize: '12px' }}>
																{product.description ? ' • ' : ''}
																{currency}
																{Number(product.default_price).toFixed(2)}
															</span>
														)}
														{!selectedProductIds.has(product.id) && (
															<Typography
																component="span"
																style={{
																	color: '#d97706',
																	fontSize: '12px',
																	fontStyle: 'italic',
																}}
															>
																{' '}
																• Will be removed
															</Typography>
														)}
													</span>
												}
											/>
											<ListItemSecondaryAction>
												{loadingProductIds.has(product.id) ? (
													<CircularProgress size={20} />
												) : (
													<Checkbox
														edge="end"
														checked={selectedProductIds.has(product.id)}
														onChange={() => handleProductToggle(product.id)}
														color="primary"
													/>
												)}
											</ListItemSecondaryAction>
										</ListItem>
									))}
								</List>
								<Divider style={{ margin: '16px 0' }} />
							</>
						)}

						{/* Other products */}
						<Typography variant="subtitle2" gutterBottom style={{ fontWeight: 'bold', color: '#374151' }}>
							Other Products ({productsNotInCategoryFiltered.length})
						</Typography>
						<List dense style={{ maxHeight: '300px', overflow: 'auto' }}>
							{productsNotInCategoryFiltered.length > 0 ? (
								productsNotInCategoryFiltered.map((product) => (
									<ListItem
										key={product.id}
										component="div"
										style={{
											backgroundColor: selectedProductIds.has(product.id)
												? '#f0f9ff'
												: 'transparent',
											borderRadius: '4px',
											marginBottom: '2px',
											cursor: 'pointer',
										}}
										onClick={() => handleProductToggle(product.id)}
									>
										<ListItemText
											primary={product.name}
											secondary={
												<span>
													{product.description && <span>{product.description}</span>}
													{product.default_price && (
														<span style={{ color: '#059669', fontSize: '12px' }}>
															{product.description ? ' • ' : ''}
															{currency}
															{Number(product.default_price).toFixed(2)}
														</span>
													)}
													{product.category_id && product.category_id !== productCategory.id && (
														<Typography
															component="span"
															style={{ color: '#6b7280', fontSize: '12px' }}
														>
															{' '}
															• In other category
														</Typography>
													)}
													{selectedProductIds.has(product.id) && (
														<Typography
															component="span"
															style={{
																color: '#059669',
																fontSize: '12px',
																fontStyle: 'italic',
															}}
														>
															{' '}
															• Will be added
														</Typography>
													)}
												</span>
											}
										/>
										<ListItemSecondaryAction>
											{loadingProductIds.has(product.id) ? (
												<CircularProgress size={20} />
											) : (
												<Checkbox
													edge="end"
													checked={selectedProductIds.has(product.id)}
													onChange={() => handleProductToggle(product.id)}
													color="primary"
												/>
											)}
										</ListItemSecondaryAction>
									</ListItem>
								))
							) : (
								<Typography
									variant="body2"
									color="textSecondary"
									style={{ padding: '20px', textAlign: 'center' }}
								>
									{searchTerm
										? 'No products found matching your search.'
										: 'All products are already in this category.'}
								</Typography>
							)}
						</List>
					</>
				)}
			</DialogContent>

			<DialogActions style={{ padding: '16px 24px' }}>
				<Button onClick={handleClose} variant="contained">
					Close
				</Button>
			</DialogActions>
		</Dialog>
	);
}
